import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import {
  Button,
  Icon,
  SafeHtmlContent,
  Text
} from '@yes.technology/react-toolkit'

import 'asset/css/form.css'
import { ActivityModelDraw } from 'types/shared'
import { ActivityInputProps } from '../Activity.types'
import {
  Agreement,
  Container,
  ImageContainer,
  LabelArea
} from './ActivityInputDraw.styles'

import { interpolateTerms, type Parameters } from 'utils/interpolate'
import TextSignatureEditor, {
  TextSignatureEditorRef
} from './TextSignatureEditor'
import CanvasSignatureEditor, {
  CanvasSignatureEditorRef
} from './CanvasSignatureEditor'

export type ActivityInputDrawProps = {
  explanation?: string
  label?: string
  readOnly?: boolean
  inputValue: string

  onChange: ActivityInputProps['handleChange']
} & Pick<ActivityModelDraw, 'agreement'>

function ActivityInputDraw({
  explanation,
  inputValue,
  label,
  agreement,
  readOnly,
  onChange
}: ActivityInputDrawProps) {
  const { t } = useTranslation()

  const values: Parameters = inputValue && JSON.parse(inputValue)

  const { base64 = '', ...params } = values

  const [mode, setMode] = useState<'text-editor' | 'canvas-editor' | 'preview'>(
    base64 || readOnly ? 'preview' : 'canvas-editor'
  )

  const [lastEditedImageData, setLastEditedImageData] = useState('')

  const canvasEditorRef = useRef<CanvasSignatureEditorRef>(null)
  const textEditorRef = useRef<TextSignatureEditorRef>(null)

  const handleSave = () => {
    setMode('preview')
    onChange(JSON.stringify({ ...params, base64: lastEditedImageData }))
  }

  const handleClear = () => {
    onChange(JSON.stringify({ ...params, base64: '' }))
    canvasEditorRef.current?.clear()
    textEditorRef.current?.clear()

    if (mode === 'preview') {
      setMode('canvas-editor')
    }
  }

  const interpolatedTerms = interpolateTerms(agreement, params)

  return (
    <Container>
      {explanation && (
        <Text variant='content-04' as='div'>
          {explanation}
        </Text>
      )}

      {interpolatedTerms && (
        <Agreement>
          <SafeHtmlContent html={interpolatedTerms} />
        </Agreement>
      )}

      <div>
        <LabelArea>
          <Text variant='label-required-03' as='label'>
            {label}
          </Text>
          {!readOnly && (
            <>
              <Button
                variant={mode === 'text-editor' ? 'primary' : 'secondary'}
                onClick={() => setMode('text-editor')}
                title={t('activity-input-draw.type-signature')}
                heightClass='small'
                aria-pressed={mode === 'text-editor'}
              >
                <Icon iconName='Text' icSize='small' />
              </Button>

              <Button
                variant={mode === 'canvas-editor' ? 'primary' : 'secondary'}
                onClick={() => setMode('canvas-editor')}
                title={t('activity-input-draw.draw-signature')}
                heightClass='small'
                aria-pressed={mode === 'canvas-editor'}
              >
                <Icon iconName='SignatureFill' icSize='small' />
              </Button>

              <Button
                variant='secondary'
                onClick={handleClear}
                title={t('activity-input-draw.clear')}
                heightClass='small'
                disabled={!base64 && !lastEditedImageData}
              >
                <Icon iconName='Cleaner' icSize='small' />
              </Button>
            </>
          )}
        </LabelArea>

        <TextSignatureEditor
          onChange={setLastEditedImageData}
          enabled={mode === 'text-editor'}
          ref={textEditorRef}
        />

        <CanvasSignatureEditor
          enabled={mode === 'canvas-editor'}
          onChange={setLastEditedImageData}
          ref={canvasEditorRef}
          {...{ base64 }}
        />

        {base64 && mode === 'preview' && (
          <ImageContainer>
            <img src={base64} data-testid='signature-img' />
          </ImageContainer>
        )}
      </div>

      {!readOnly && (
        <Button
          heightClass='regular'
          onClick={handleSave}
          disabled={lastEditedImageData === '' || mode === 'preview'}
        >
          {t('activity-input-draw.confirm')}
        </Button>
      )}
    </Container>
  )
}

export default ActivityInputDraw
